import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Match } from '../types';
import { useResponsive } from '../hooks/useResponsive';

interface MatchHistoryCardProps {
  match: Match;
  userStats?: {
    goals: number;
    assists: number;
    saves: number;
    position: string;
    teamName: string;
    isMVP: boolean;
    isTopScorer: boolean;
    isTopAssister: boolean;
    isTopSaver: boolean;
    matchResult?: 'WIN' | 'LOSS' | 'DRAW';
    hasPendingValidations?: boolean;
    pendingValidationsCount?: number;
  };
}

export const MatchHistoryCard: React.FC<MatchHistoryCardProps> = ({ match, userStats }) => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  const getPositionEmoji = (position: string) => {
    switch (position) {
      case 'Arquero': return '🧤';
      case 'Defensor': return '🛡️';
      case 'Mediocampista': return '⚡';
      case 'Delantero': return '⚽';
      default: return '👤';
    }
  };

  const getMatchResultColor = (matchResult?: 'WIN' | 'LOSS' | 'DRAW') => {
    switch (matchResult) {
      case 'WIN': return '#00E676';
      case 'LOSS': return '#FF6B6B';
      case 'DRAW': return '#FFD600';
      default: return '#B0BEC5';
    }
  };

  const getMatchResultText = (matchResult?: 'WIN' | 'LOSS' | 'DRAW') => {
    switch (matchResult) {
      case 'WIN': return 'Victoria';
      case 'LOSS': return 'Derrota';
      case 'DRAW': return 'Empate';
      default: return 'Sin resultado';
    }
  };

  return (
    <button
      type="button"
      onClick={() => navigate(`/match/${match.id}`)}
      style={{
        backgroundColor: '#23272A',
        borderRadius: '16px',
        border: '1px solid #33383B',
        padding: isMobile ? '16px' : '20px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '12px' : '16px',
        minHeight: isMobile ? 'auto' : '200px',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        overflow: 'hidden',
        textAlign: 'left'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#2C2F33';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#23272A';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)';
      }}
    >
      {/* Header con fecha y resultado */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: isMobile ? '8px' : '12px',
        gap: '12px'
      }}>
        <div style={{
          flex: 1,
          minWidth: 0 // Permite que el contenido se contraiga
        }}>
          <div style={{
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 'bold',
            color: '#FFFFFF',
            marginBottom: '4px'
          }}>
            {formatDate(match.date)} · {formatTime(match.time)}
          </div>
          <div style={{
            fontSize: isMobile ? '12px' : '14px',
            color: '#B0BEC5',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}>
            📍 {match.location}
          </div>
        </div>
        {userStats?.matchResult && (
          <span style={{
            padding: isMobile ? '4px 8px' : '6px 12px',
            borderRadius: '8px',
            fontSize: isMobile ? '11px' : '12px',
            fontWeight: 'bold',
            backgroundColor: getMatchResultColor(userStats.matchResult),
            color: userStats.matchResult === 'WIN' ? '#181A1B' : '#FFFFFF',
            flexShrink: 0, // Evita que se contraiga
            whiteSpace: 'nowrap' // Evita que el texto se rompa
          }}>
            {getMatchResultText(userStats.matchResult)}
          </span>
        )}
        {userStats?.hasPendingValidations && (
          <span style={{
            padding: isMobile ? '2px 6px' : '3px 8px',
            borderRadius: '6px',
            fontSize: isMobile ? '9px' : '10px',
            fontWeight: 'bold',
            backgroundColor: '#FF9800',
            color: '#FFFFFF',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            marginLeft: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '2px'
          }}>
            ⏳ Reportes
          </span>
        )}
      </div>

      {/* Equipos */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: isMobile ? '12px' : '16px',
        backgroundColor: '#2C2F33',
        borderRadius: '12px',
        border: '1px solid #40444B',
        width: '100%',
        boxSizing: 'border-box',
        gap: '8px' // Espacio entre elementos
      }}>
        <div style={{
          textAlign: 'center',
          flex: '1 1 0', // Permite que se contraiga y crezca equitativamente
          minWidth: 0, // Permite que el contenido se contraiga
          maxWidth: '100%', // Limita el ancho máximo
          overflow: 'hidden' // Evita desbordamiento
        }}>
          <div style={{
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: 'bold',
            color: '#FFFFFF',
            marginBottom: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            padding: '0 4px',
            width: '100%',
            maxWidth: '100%'
          }}>
            {match.team1Name}
          </div>
          {match.team1Goals !== null && match.team1Goals !== undefined && match.team1Goals > 0 && (
            <div style={{
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: 'bold',
              color: '#00E676'
            }}>
              {match.team1Goals > (match.team2Goals || 0) ? `+${match.team1Goals}` : match.team1Goals}
            </div>
          )}
        </div>
        <div style={{
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: 'bold',
          color: '#B0BEC5',
          flexShrink: 0, // Evita que VS se contraiga
          whiteSpace: 'nowrap' // Evita que VS se rompa
        }}>
          VS
        </div>
        <div style={{
          textAlign: 'center',
          flex: '1 1 0', // Permite que se contraiga y crezca equitativamente
          minWidth: 0, // Permite que el contenido se contraiga
          maxWidth: '100%', // Limita el ancho máximo
          overflow: 'hidden' // Evita desbordamiento
        }}>
          <div style={{
            fontSize: isMobile ? '16px' : '18px',
            fontWeight: 'bold',
            color: '#FFFFFF',
            marginBottom: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            padding: '0 4px',
            width: '100%',
            maxWidth: '100%'
          }}>
            {match.team2Name}
          </div>
          {match.team2Goals !== null && match.team2Goals !== undefined && match.team2Goals > 0 && (
            <div style={{
              fontSize: isMobile ? '20px' : '24px',
              fontWeight: 'bold',
              color: '#00E676'
            }}>
              {match.team2Goals > (match.team1Goals || 0) ? `+${match.team2Goals}` : match.team2Goals}
            </div>
          )}
        </div>
      </div>

      {/* Información del jugador */}
      {userStats && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '8px' : '12px'
        }}>
          {/* Equipo y posición */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: isMobile ? '8px 12px' : '10px 16px',
            backgroundColor: '#2C2F33',
            borderRadius: '8px',
            border: '1px solid #40444B'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: isMobile ? '14px' : '16px' }}>
                {getPositionEmoji(userStats.position)}
              </span>
              <span style={{
                fontSize: isMobile ? '13px' : '14px',
                color: '#FFFFFF',
                fontWeight: '600'
              }}>
                {userStats.position}
              </span>
            </div>
            <div style={{
              fontSize: isMobile ? '13px' : '14px',
              color: '#00E676',
              fontWeight: '600'
            }}>
              {userStats.teamName}
            </div>
          </div>

          {/* Estadísticas */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            padding: isMobile ? '8px 12px' : '10px 16px',
            backgroundColor: '#2C2F33',
            borderRadius: '8px',
            border: '1px solid #40444B'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: 'bold',
                color: '#FFFFFF'
              }}>
                {userStats.goals}
              </div>
              <div style={{
                fontSize: isMobile ? '11px' : '12px',
                color: '#B0BEC5'
              }}>
                ⚽ Goles
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: 'bold',
                color: '#FFFFFF'
              }}>
                {userStats.assists}
              </div>
              <div style={{
                fontSize: isMobile ? '11px' : '12px',
                color: '#B0BEC5'
              }}>
                ➡️ Asistencias
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: 'bold',
                color: '#FFFFFF'
              }}>
                {userStats.saves}
              </div>
              <div style={{
                fontSize: isMobile ? '11px' : '12px',
                color: '#B0BEC5'
              }}>
                🧤 Atajadas
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Logros - Movidos al final */}
      {userStats && (userStats.isMVP || userStats.isTopScorer || userStats.isTopAssister || userStats.isTopSaver) && (
        <div style={{
          display: 'flex',
          gap: '6px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          {userStats.isMVP && (
            <span style={{
              backgroundColor: '#FFD600',
              color: '#181A1B',
              padding: isMobile ? '4px 8px' : '6px 10px',
              borderRadius: '8px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: 'bold'
            }}>
              👑 MVP
            </span>
          )}
          {userStats.isTopScorer && (
            <span style={{
              backgroundColor: '#00E676',
              color: '#181A1B',
              padding: isMobile ? '4px 8px' : '6px 10px',
              borderRadius: '8px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: 'bold'
            }}>
              ⚽ Goleador
            </span>
          )}
          {userStats.isTopAssister && (
            <span style={{
              backgroundColor: '#7C4DFF',
              color: '#FFFFFF',
              padding: isMobile ? '4px 8px' : '6px 10px',
              borderRadius: '8px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: 'bold'
            }}>
              ➡️ Asistidor
            </span>
          )}
          {userStats.isTopSaver && (
            <span style={{
              backgroundColor: '#00B8D9',
              color: '#181A1B',
              padding: isMobile ? '4px 8px' : '6px 10px',
              borderRadius: '8px',
              fontSize: isMobile ? '11px' : '12px',
              fontWeight: 'bold'
            }}>
              🧤 Guante de Oro
            </span>
          )}
        </div>
      )}
    </button>
  );
}; 